import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {chatApi} from "./chatApi";
import {
    RagSourceMessage,
    ThreadNameMessage,
    ChatStopReason,
    isAssistantMessage,
    ThreadWithMetadata,
    MessageWithMetadata,
    SourceInfo,
    AgentThought,
    AgentActivity,
    PluginActivity,
    LocalStorageKeys,
    KAIResponseType,
    RelatedToolsMessage,
} from "../../types";

const initialState: Partial<ThreadWithMetadata> & {
    isStreaming: boolean;
} = {
    threadId: undefined,
    name: undefined,
    messages: [],
    lastUpdated: undefined,
    regenerationCount: 0,
    isStreaming: false,
};

const threadSlice = createSlice({
    name: "thread",
    initialState,
    reducers: {
        /*         testSetStopReason(state, action: PayloadAction<ChatStopReason>) {
            const lastMessage = state.messages[state.messages.length - 1];
            if (lastMessage?.role === "assistant") {
                lastMessage.stopReason = action.payload;
            }
        },
 */
        rateLimitReset(state) {
            state.isStreaming = true;
            const lastMessage = state.messages?.[state.messages.length - 1];
            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.sources = [];
                lastMessage.content = "";
            }
        },
        finishAssistantMessage(
            state,
            action: PayloadAction<{
                text: string;
                stopReason?: ChatStopReason;
            }>
        ) {
            // Set the last assistant message to the finalized response
            const lastMessage = state.messages?.[state.messages.length - 1];

            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.content = action.payload.text;

                try {
                    const json = JSON.parse(lastMessage.content);
                    const citations = json.citations;
                    if (citations && lastMessage.sources) {
                        lastMessage.consideredSources =
                            lastMessage.sources.filter(
                                (source: SourceInfo) =>
                                    !citations.includes(source.index)
                            );
                    }
                } catch (e) {
                    console.error(e);
                }
                /* lastMessage.stopReason = action.payload.stopReason; */
            }

            state.isStreaming = false;
        },
        addPartialMessage(state, action: PayloadAction<string>) {
            const lastMessage = state.messages?.[state.messages.length - 1];

            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.content = action.payload;
            }
        },
        replaceMessage(state, action: PayloadAction<string>) {
            const lastMessage = state.messages?.[state.messages.length - 1];

            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.content = action.payload;
            }
        },
        addAgentThought(state, action: PayloadAction<AgentThought>) {
            const lastMessage = state.messages?.[state.messages.length - 1];

            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.thoughts = lastMessage.thoughts ?? [];
                lastMessage.thoughts.push(action.payload);
            }
        },
        addAgentActivity(state, action: PayloadAction<AgentActivity>) {
            const lastMessage = state.messages?.[state.messages.length - 1];

            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.activities = lastMessage.activities ?? [];
                lastMessage.activities.push(action.payload);
            }
        },
        addPluginActivity(state, action: PayloadAction<PluginActivity>) {
            const lastMessage = state.messages?.[state.messages.length - 1];
            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.pluginActivities =
                    lastMessage.pluginActivities ?? [];
                lastMessage.pluginActivities.push(action.payload);
            }
        },
        addMessage(state, action: PayloadAction<MessageWithMetadata>) {
            state.messages?.push(action.payload);
        },
        addSourceToLastMessage(state, action: PayloadAction<RagSourceMessage>) {
            const lastMessage = state.messages?.[state.messages.length - 1];
            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.sources = lastMessage.sources ?? [];
                // Map RagSourceMessage to SourceInfo (adjust field names as needed)
                lastMessage.sources.push({
                    // Provide default values for potentially undefined fields
                    index: action.payload.index,
                    id: action.payload.topicid ?? "",
                    name: action.payload.title ?? "",
                    segmentId: action.payload.segmentid ?? "",
                    segmentName: action.payload.segmentName ?? "",
                    type: action.payload.topictype ?? "",
                    description: action.payload.description ?? "",
                    url: action.payload.url ?? "",
                });
            }
        },
        addRelatedToolsToLastMessage(
            state,
            action: PayloadAction<RelatedToolsMessage>
        ) {
            const lastMessage = state.messages?.[state.messages.length - 1];
            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.relatedTools = lastMessage.relatedTools ?? [];
                // Map RagSourceMessage to SourceInfo (adjust field names as needed)
                lastMessage.relatedTools.push({
                    // Provide default values for potentially undefined fields
                    index: action.payload.index,
                    id: action.payload.topicid ?? "",
                    title: action.payload.title ?? "",
                    type: action.payload.topictype ?? "",
                    url: action.payload.url ?? "",
                    description: action.payload.description ?? "",
                    score: action.payload.score ?? 0,
                    isFile: action.payload.isFile ?? false,
                    updatedAt: action.payload.updatedAt ?? "",
                });
            }
        },
        addConsideredSourcesToLastMessage(
            state,
            action: PayloadAction<RagSourceMessage>
        ) {
            const lastMessage = state.messages?.[state.messages.length - 1];
            if (lastMessage && isAssistantMessage(lastMessage)) {
                lastMessage.consideredSources =
                    lastMessage.consideredSources ?? [];
                lastMessage.consideredSources.push({
                    index: action.payload.index,
                    id: action.payload.topicid ?? "",
                    name: action.payload.title ?? "",
                    segmentId: action.payload.segmentid ?? "",
                    segmentName: action.payload.segmentName ?? "",
                    type: action.payload.topictype ?? "",
                    url: action.payload.url ?? "",
                });
            }
        },
        removeMessagesFromIndex(state, action: PayloadAction<number>) {
            const index = state.messages?.findIndex(
                (m) => m.position === action.payload
            );

            if (index !== -1) {
                const i = index ?? action.payload;
                state.messages?.splice(i, state.messages?.length - i);
            }
        },
        updateName(state, action: PayloadAction<string>) {
            state.name = action.payload;
        },
        updateThreadId(state, action: PayloadAction<string>) {
            state.threadId = action.payload;
        },
        updateThread(
            state,
            action: PayloadAction<{
                id: string;
                name: string;
                messages: MessageWithMetadata[];
                datestamp: string;
            }>
        ) {
            const {id, name, messages, datestamp} = action.payload;

            state.threadId = id;
            state.name = name;
            state.lastUpdated = datestamp;
            // Replace the messages without IDs with the new ones
            // This way we handle the first temporary message from the user
            // that doesnt contain any ID
            state.messages = [
                ...(state.messages?.filter((m) => !!m.position) ?? []),
                ...messages,
            ];
        },
        updateThreadName(state, action: PayloadAction<ThreadNameMessage>) {
            if (state.threadId === action.payload.threadId) {
                state.name = action.payload.threadName;
            }
        },
        /*         addRagProcess(state, action: PayloadAction<RagProcessMessage>) {
            const lastMessage = state.messages?.[state.messages.length - 1];

            if (lastMessage && lastMessage.ragProcesses) {
                lastMessage.ragProcesses.push(action.payload);
            } else {
                lastMessage.ragProcesses = [action.payload];
            }
        }, */
        /*         addRagSource(state, action: PayloadAction<RagSourceMessage>) {
            const lastMessage = state.messages[state.messages.length - 1];

            if (lastMessage.ragSources) {
                lastMessage.ragSources.push(action.payload);
            } else {
                lastMessage.ragSources = [action.payload];
            }
        }, */
        /*         addUsage(state, action: PayloadAction<UsageMessage>) {
            const lastMessage = state.messages[state.messages.length - 1];
            if (!lastMessage?.usage?.[action.payload.model]) {
                // @ts-ignore
                lastMessage.usage = {
                    ...(lastMessage?.usage ?? {}),
                    [action.payload.model]: {
                        completion_tokens: 0,
                        prompt_tokens: 0,
                        total_tokens: 0,
                    },
                };
            }

            lastMessage.usage[action.payload.model] = {
                completion_tokens:
                    (lastMessage.usage?.[action.payload.model]
                        ?.completion_tokens ?? 0) +
                    action.payload.completion_tokens,
                prompt_tokens:
                    (lastMessage.usage?.[action.payload.model]?.prompt_tokens ??
                        0) + action.payload.prompt_tokens,
                total_tokens:
                    (lastMessage.usage?.[action.payload.model]?.total_tokens ??
                        0) + action.payload.total_tokens,
            };
        },
 */ /* addFilteredContentError(
            state,
            action: PayloadAction<FilteredContentMessage>
        ) {
            state.filteredContent = action.payload;
        }, */
        clearThread(state) {
            return {
                ...initialState,
            };
        },
        /* setSystem(state, action: PayloadAction<System>) {
            state.system = action.payload;
        }, */
        /*         addProcessingTime(
            state,
            action: PayloadAction<{ragStart: number; ragEnd: number}>
        ) {
            const lastMessage = state.messages[state.messages.length - 1];

            try {
                lastMessage.ragProcessStart = new Date(action.payload.ragStart);
                lastMessage.ragProcessEnd = new Date(action.payload.ragEnd);
            } catch (e) {
                console.error(e);
            }
        },
        addFollowUpQuestions(state, action: PayloadAction<string[]>) {
            const lastMessage = state.messages[state.messages.length - 1];
            lastMessage.followUpQuestions = action.payload;
        }, */
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            chatApi.endpoints.sendMessage.matchPending,
            (state, action) => {
                /* const id = action.meta.arg.originalArgs.messageId;

                // If the message is a resend, slice the messages up to the message
                if (id) {
                    const i = state.messages?.findIndex(
                        (m) => m.position === id
                    );

                    if (i !== -1) {
                        state.messages = state.messages.slice(0, i);
                    }
                } */

                state.isStreaming = true;
                /* state.filteredContent = null; */
            }
        );
        builder.addMatcher(
            chatApi.endpoints.getThread.matchFulfilled,
            (state, action) => {
                // TODO: understand why this is necessary
                /*  old
                if (!state.isStreaming) {
                    state.messages = action.payload?.messages ?? [];
                    state.id = action.payload?.id;
                    state.name = action.payload?.name;
                    state.datestamp = action.payload?.datestamp;
                } */
                if (!state.isStreaming) {
                    state.messages = action.payload?.messages ?? [];
                    state.threadId = action.payload?.threadId;
                    state.name = action.payload?.name;
                    state.lastUpdated = action.payload?.lastUpdated;
                    state.regenerationCount = action.payload?.regenerationCount;
                    state.isStreaming = false;
                }
            }
        );
        builder.addMatcher(
            chatApi.endpoints.rateMessage.matchFulfilled,
            (state, action) => {
                const messagePosition =
                    action.meta.arg.originalArgs.messagePosition;
                const rating = action.meta.arg.originalArgs.rating;
                const comment = action.meta.arg.originalArgs.comment;

                const message = state.messages?.[messagePosition];
                if (message) {
                    message.rating = rating;
                    message.feedback = comment;
                }
            }
        );
        /* builder.addMatcher(
            chatApi.endpoints.getUserThreads.matchFulfilled,
            (state, action) => {
                action.payload.forEach((thread) => {
                    state.threads[thread.id] = thread;
                });
            }
        ); */
    },
});

export const {
    finishAssistantMessage,
    addPartialMessage,
    replaceMessage,
    addAgentThought,
    addAgentActivity,
    addPluginActivity,
    updateThread,
    updateThreadId,
    updateName,
    updateThreadName,
    removeMessagesFromIndex,
    /* addRagProcess,
    addRagSource,
    addUsage, */
    addMessage,
    rateLimitReset,
    /* addFilteredContentError,
    addProcessingTime,
    setSystem, */
    addSourceToLastMessage,
    addRelatedToolsToLastMessage,
    clearThread,
    /* addFollowUpQuestions, */
} = threadSlice.actions;

export default threadSlice.reducer;
